/**
 * PendingInvitationsSection — therapist-side list of invitations the
 * therapist has created that haven't been used yet (or are expired /
 * revoked, with filters).
 *
 * Backend already scopes GET /api/invitations to the caller's own
 * createdBy when the role is therapist, so we can fetch unfiltered.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Tooltip, CircularProgress,
  Alert, Button, Snackbar, Stack,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import BlockIcon from '@mui/icons-material/Block';
import api from '../../services/api';

const STATUS_LABELS = {
  active:    { label: 'Pending',  color: 'warning' },
  expired:   { label: 'Expired',  color: 'default' },
  used:      { label: 'Accepted', color: 'success' },
  revoked:   { label: 'Revoked',  color: 'default' },
  inactive:  { label: 'Inactive', color: 'default' },
  depleted:  { label: 'Depleted', color: 'default' },
};

const formatDate = (s) => s ? new Date(s).toLocaleString() : '—';

function PendingInvitationsSection({ token, refreshKey = 0 }) {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState({}); // per-invitation action lock
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Therapist role is auto-scoped to createdBy=self by the backend.
      // Filter to individual invitations + active/expired statuses; we
      // hide 'used' here because those become assigned clients in the
      // table below.
      const res = await api.get(
        '/invitations?type=individual&status=active,expired&limit=50&sortBy=createdAt&sortOrder=desc',
        token
      );
      const list = res?.data?.invitations || res?.invitations || [];
      setInvitations(list);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load invitations');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const setBusyFor = (id, value) =>
    setBusy(prev => ({ ...prev, [id]: value }));

  const copyLink = async (inv) => {
    if (!inv.invitationUrl) {
      setToast({ message: 'Invitation has no URL', severity: 'error' });
      return;
    }
    try {
      await navigator.clipboard.writeText(inv.invitationUrl);
      setToast({ message: 'Link copied to clipboard', severity: 'success' });
    } catch (_) {
      window.prompt('Copy this link:', inv.invitationUrl);
    }
  };

  const resendInvitation = async (inv) => {
    setBusyFor(inv._id, 'resend');
    try {
      await api.post(`/invitations/${inv._id}/resend`, {}, token);
      setToast({ message: `Email re-sent to ${inv.invitedEmail}`, severity: 'success' });
      await load();
    } catch (err) {
      setToast({
        message: err?.response?.data?.message || err?.message || 'Resend failed',
        severity: 'error',
      });
    } finally {
      setBusyFor(inv._id, false);
    }
  };

  const regenerateLink = async (inv) => {
    if (!window.confirm('Regenerate the link? The previous link will stop working.')) return;
    setBusyFor(inv._id, 'regenerate');
    try {
      await api.post(`/invitations/${inv._id}/regenerate`, {}, token);
      setToast({ message: 'Link regenerated', severity: 'success' });
      await load();
    } catch (err) {
      setToast({
        message: err?.response?.data?.message || err?.message || 'Regenerate failed',
        severity: 'error',
      });
    } finally {
      setBusyFor(inv._id, false);
    }
  };

  const revokeInvitation = async (inv) => {
    if (!window.confirm(`Revoke the invitation for ${inv.invitedName}? This cannot be undone.`)) return;
    setBusyFor(inv._id, 'revoke');
    try {
      await api.post(`/invitations/${inv._id}/revoke`, {}, token);
      setToast({ message: 'Invitation revoked', severity: 'success' });
      await load();
    } catch (err) {
      setToast({
        message: err?.response?.data?.message || err?.message || 'Revoke failed',
        severity: 'error',
      });
    } finally {
      setBusyFor(inv._id, false);
    }
  };

  if (loading && invitations.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={20} />
      </Paper>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>;
  }

  if (invitations.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{ mb: 3 }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle1" fontWeight={600}>Pending invitations</Typography>
          <Chip size="small" label={invitations.length} />
        </Stack>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={load}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invitations.map(inv => {
              const meta = STATUS_LABELS[inv.status] || { label: inv.status, color: 'default' };
              const isBusy = !!busy[inv._id];
              return (
                <TableRow key={inv._id} hover>
                  <TableCell>{inv.invitedName}</TableCell>
                  <TableCell>{inv.invitedEmail}</TableCell>
                  <TableCell>
                    <Chip size="small" label={meta.label} color={meta.color} variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(inv.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(inv.expiresAt)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Copy invitation link">
                      <span>
                        <IconButton size="small" onClick={() => copyLink(inv)} disabled={isBusy}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Re-send invitation email">
                      <span>
                        <IconButton size="small" onClick={() => resendInvitation(inv)} disabled={isBusy || inv.status !== 'active'}>
                          <SendIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Regenerate link (invalidates previous)">
                      <span>
                        <IconButton size="small" onClick={() => regenerateLink(inv)} disabled={isBusy}>
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Revoke">
                      <span>
                        <IconButton size="small" color="error" onClick={() => revokeInvitation(inv)} disabled={isBusy || inv.status === 'revoked'}>
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast && (
          <Alert severity={toast.severity || 'info'} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        )}
      </Snackbar>
    </Paper>
  );
}

export default PendingInvitationsSection;
